import type { HTMLAttributes, MouseEvent, ReactElement, ReactNode } from 'react';
import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import type { To } from 'react-router-dom';
import { Link } from 'react-router-dom';

import {
    arrow,
    autoPlacement,
    offset,
    safePolygon,
    size,
    useFloating,
    useHover,
    useInteractions,
} from '@floating-ui/react';
import { faAwardSimple, faChevronRight } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { ClassValue } from 'clsx';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

import { useSideBarContext } from '@spacefill/shared/src/utils/contexts/SideBarContext';

import { ChipNumber } from '../ChipNumber';
import { CompanyLogo } from '../CompanyLogo';

export interface LinkWrapperProps {
    href?: string;
    to?: To;
    target?: string;
    onClick?: (ev: MouseEvent<HTMLAnchorElement>) => void;
    children?: ReactNode;
    dataTestid?: string;
    className?: string;
}

function LinkWrapper({ to, href, target, onClick, children, dataTestid, className }: LinkWrapperProps) {
    if (to)
        return (
            <Link
                to={to}
                className={className}
                data-testid={dataTestid}
            >
                {children}
            </Link>
        );

    if (href)
        return (
            <a
                href={href}
                target={target}
                className={className}
                data-testid={dataTestid}
                onClick={onClick}
            >
                {children}
            </a>
        );
    else
        return (
            <a
                className={className}
                data-testid={dataTestid}
                onClick={onClick}
            >
                {children}
            </a>
        );
}

export interface NavItemProps {
    disabled?: boolean;
    isActive?: boolean;
    isProFeature?: boolean;
    href?: string;
    to?: To;
    target?: string;
    onClick?: (ev: MouseEvent<HTMLAnchorElement>) => void;
    title: string;
    className?: ClassValue;
    children?: ReactNode;
    icon?: ReactElement<typeof FontAwesomeIcon>;
    hasRoundedIcon?: boolean;
    bottomDivider?: boolean;
    dataTestid?: string;
    htmlAttributes?: HTMLAttributes<HTMLLIElement>;
    logoUrl?: string;
    countValue?: number;
    isSubNav?: boolean;
    conditionForDisplay?: () => boolean;
    isNew?: boolean;
    isMouseInSidebar?: boolean;
}

export const NavItem = ({
    disabled = false,
    isProFeature = false,
    isActive = false,
    title,
    className,
    children,
    icon,
    hasRoundedIcon = false,
    bottomDivider = false,
    dataTestid = 'nav-item',
    logoUrl = undefined,
    countValue = undefined,
    onClick,
    to,
    href,
    target,
    htmlAttributes,
    isSubNav = false,
    isNew = false,
    isMouseInSidebar = false,
}: NavItemProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [maxHeight, setMaxHeight] = useState<number | null>(null);
    const arrowRef = useRef(null);
    const { isSizeBarCollapsed = false } = useSideBarContext();

    const { refs, floatingStyles, context } = useFloating({
        open: isOpen,
        onOpenChange: setIsOpen,
        placement: 'right-start',
        strategy: 'fixed',
        middleware: [
            autoPlacement({ alignment: 'start', allowedPlacements: ['right-start', 'right-end'] }),
            offset({ mainAxis: 15, alignmentAxis: -8 }),
            size({
                apply({ availableHeight }) {
                    flushSync(() => setMaxHeight(availableHeight - 10));
                },
            }),
            arrow({
                element: arrowRef,
            }),
        ],
    });

    const hover = useHover(context, {
        delay: { open: 75 },
        enabled: !!children,
        handleClose: safePolygon({
            blockPointerEvents: true, // Avoid hover events on the sibling elements during move to be triggered
        }),
    });
    const { getReferenceProps, getFloatingProps } = useInteractions([hover]);

    return (
        <li
            className={clsx('flex-1 cursor-pointer list-none transition-all', {
                'relative before:absolute before:-left-2 before:top-1/2 before:-translate-y-1/2 before:h-4.5 before:w-0.75 before:rounded-r-sm before:bg-button-background hover:text-button-background':
                    isActive,
            })}
            data-component-name='NavItem'
            data-active={isActive}
            {...htmlAttributes}
        >
            <div
                ref={refs.setReference}
                {...getReferenceProps()}
            >
                <LinkWrapper
                    dataTestid={dataTestid}
                    className={twMerge(
                        clsx(
                            'relative flex font-medium items-center gap-2.5 whitespace-nowrap rounded-md px-3 py-1.5 text-sm text-sidebar-foreground/80 transition-all [&_svg]:size-4',
                            {
                                'hover:text-sidebar-foreground': !isActive,
                                'text-zinc-500 cursor-not-allowed': disabled,
                                'cursor-not-allowed': isProFeature,
                                'bg-white/5 text-sidebar-foreground/80': isOpen && children,
                                'text-button-background [&_svg]:text-button-background': isActive,
                                'bg-button-background/30': isActive && !isMouseInSidebar,
                                'relative mb-4 after:-bottom-2 after:content-[""] after:absolute after:border-b after:border-solid after:border-zinc-600 after:inset-x-0':
                                    bottomDivider,
                                'justify-center': isSizeBarCollapsed && !isSubNav,
                            },
                            className
                        )
                    )}
                    to={to}
                    href={href}
                    target={target}
                    onClick={onClick}
                >
                    {logoUrl && (
                        <CompanyLogo
                            logoUrl={logoUrl}
                            isActive={isActive}
                            variant='small'
                        />
                    )}
                    {icon && !logoUrl && (
                        <div
                            className={clsx({
                                'bg-white/10 rounded-full p-2 size-8 text-sidebar-foreground flex items-center justify-center':
                                    hasRoundedIcon,
                            })}
                        >
                            {icon}
                        </div>
                    )}
                    {(!isSizeBarCollapsed || (isSizeBarCollapsed && isSubNav)) && title}
                    {isProFeature && (
                        <FontAwesomeIcon
                            icon={faAwardSimple}
                            className='text-violet-400'
                        />
                    )}
                    {!!countValue && (
                        <ChipNumber
                            variant='red'
                            className='ml-auto text-[.625rem]'
                            rounded
                            value={countValue}
                        />
                    )}
                    {children && !isSizeBarCollapsed && (
                        <FontAwesomeIcon
                            icon={faChevronRight}
                            className='ml-auto'
                        />
                    )}
                    {isNew && (
                        <span className='bg-indigo-500 uppercase text-white text-[10px] px-1.5 py-px rounded-full ml-auto'>
                            New
                        </span>
                    )}
                </LinkWrapper>
            </div>

            {isOpen && children && (
                <div
                    ref={refs.setFloating}
                    className='flex rounded-md bg-sidebar-background p-2'
                    style={{ ...floatingStyles, ...(maxHeight ? { maxHeight } : {}) }}
                    {...getFloatingProps()}
                    onClick={() => setIsOpen(false)}
                >
                    <div className='overflow-y-auto'>{children}</div>
                </div>
            )}
        </li>
    );
};
